import { z } from "zod";

/**
 * Валидирует ИСХОДЯЩЕЕ тело запроса, НЕ выбрасывая неизвестные поля.
 *
 * `schema.parse(data)` в Zod по умолчанию STRIP'ает ключи, которых нет в схеме.
 * Для request-DTO это тихая потеря данных: поле добавили в форму/бэкенд, но забыли
 * в РУЧНУЮ схему — оно молча улетает пустотой, без всякой ошибки (см. баги
 * Arena `arenaTeamMode`, `requireRiotAccount`, `description`/`prize` и т.п.).
 *
 * Здесь применяем `.passthrough()` (shallow — ровно то, что нужно для плоских DTO):
 * валидация известных полей сохраняется, но любые лишние top-level поля доходят до
 * бэкенда. Бэкенд — финальный источник истины по валидации.
 *
 * Для refined-схем (ZodEffects, у которых нет `.passthrough`) — безопасный фолбэк на
 * обычный parse.
 *
 * ВАЖНО: используйте ТОЛЬКО для исходящих тел запросов. Для разбора ответов это не нужно.
 * Не возвращайтесь к голому `RequestSchema.parse(body)` на исходящих — это и есть баг.
 */
export function parseRequestLoose<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
): z.infer<T> {
  // KAN-1317: схемы API обёрнуты в z.lazy (строятся при первом разборе). У ZodLazy нет
  // `.passthrough` — без разворота лишние поля снова молча вырезались бы.
  const inner = schema instanceof z.ZodLazy ? (schema.unwrap() as z.ZodTypeAny) : schema;
  const maybe = inner as unknown as { passthrough?: () => T };
  const loose = typeof maybe.passthrough === "function" ? maybe.passthrough() : inner;
  return loose.parse(data) as z.infer<T>;
}
